import React from "react";
import {
  RiThumbUpLine,
  RiFlashlightLine,
  RiGroupLine,
  RiLightbulbFlashLine,
} from "@remixicon/react";
import { Typography } from "../../../atoms";
import styles from "./index.style.module.css";

export interface StickyRecommendationsCardProps {
  audienceFitCount: number;
  audienceFitTotal: number;
  contentQualityCount: number;
  contentQualityTotal: number;
  onApplyClick?: () => void;
  className?: string;
}

const StickyRecommendationsCard: React.FC<StickyRecommendationsCardProps> = ({
  audienceFitCount,
  audienceFitTotal,
  contentQualityCount,
  contentQualityTotal,
  onApplyClick,
  className = "",
}) => {
  const totalSelected = audienceFitCount + contentQualityCount;
  const totalRecommendations = audienceFitTotal + contentQualityTotal;
  const isApplyDisabled = totalSelected === 0;
  const shouldShowAudienceFit = audienceFitTotal > 0;
  const shouldShowContentQuality = contentQualityTotal > 0;
  const hasNoRecommendations = totalRecommendations === 0;

  // Empty state - when there are no recommendations at all
  if (hasNoRecommendations) {
    return (
      <div
        className={`${styles.stickyCard} ${styles.stickyCardEmpty} ${className}`}
      >
        <div className={styles.noRecommendationsHeader}>
          <div className={styles.headerTitleGroup}>
            <RiThumbUpLine size={16} color="#3F3F46" />
            <Typography
              variant="text-lg"
              weight="bold"
              className={styles.title}
            >
              Recommendations
            </Typography>
          </div>
        </div>

        <div className={styles.emptyStateMessage}>
          <Typography variant="text-xs" className={styles.emptyStateText}>
            Your content looks healthy and no recommendations available for
            improvements.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.stickyCard} ${className}`}>
      <div className={styles.header}>
        <div className={styles.headerTitleGroup}>
          <RiThumbUpLine size={16} color="#3F3F46" />
          <Typography variant="text-lg" weight="bold" className={styles.title}>
            Recommendations
          </Typography>
        </div>
        <Typography variant="text-xs" className={styles.selectedCount}>
          ({totalSelected}/{totalRecommendations} selected)
        </Typography>
      </div>

      <div className={styles.contentRow}>
        <div className={styles.indicatorsSection}>
          {shouldShowAudienceFit && (
            <div className={styles.indicatorGroup}>
              <Typography
                variant="text-xs"
                weight="bold"
                className={styles.indicatorLabel}
              >
                Audience Fit
              </Typography>
              <div className={styles.iconRow}>
                {Array.from({ length: audienceFitTotal }).map((_, index) => {
                  const isFilled = index < audienceFitCount;
                  return (
                    <div
                      key={`audience-${isFilled ? "filled" : "empty"}-${index}`}
                      className={styles.iconWrapper}
                    >
                      <RiGroupLine
                        size={16}
                        color={isFilled ? "#0000C9" : "#D4D4D8"}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {shouldShowAudienceFit && shouldShowContentQuality && (
            <div className={styles.divider} />
          )}

          {shouldShowContentQuality && (
            <div className={styles.indicatorGroup}>
              <Typography
                variant="text-xs"
                weight="bold"
                className={styles.indicatorLabel}
              >
                Content Quality
              </Typography>
              <div className={styles.iconRow}>
                {Array.from({ length: contentQualityTotal }).map((_, index) => {
                  const isFilled = index < contentQualityCount;
                  return (
                    <div
                      key={`quality-${isFilled ? "filled" : "empty"}-${index}`}
                      className={styles.iconWrapper}
                    >
                      <RiLightbulbFlashLine
                        size={16}
                        color={isFilled ? "#0000C9" : "#D4D4D8"}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onApplyClick}
          disabled={isApplyDisabled}
          className={`${styles.applyButton} ${
            isApplyDisabled ? styles.applyButtonDisabled : ""
          }`}
        >
          <RiFlashlightLine size={16} className={styles.applyIcon} />
          <span>Apply ({totalSelected})</span>
        </button>
      </div>

      <div className={styles.bottomDivider} />
    </div>
  );
};

export default StickyRecommendationsCard;
